/**
 * planGraphData — map an event-sourced plan workspace (DictWorkspaceState) into
 * PlanGraphView's grid model (PlanNodeData[] + PlanEdgeData[]).
 *
 * Shared by every plan-graph mount point (BuildArtifactsView plan phase + the
 * App.tsx workspace/graph path) so the plan renders identically wherever it is
 * shown. Pure / view-layer — no data, event, or backend change.
 *
 * Plan nodes are LogicNodes: they carry `validation_status`, NOT a lifecycle
 * status, so the display status is derived from validation_status.
 */

import type { DictNode, DictWorkspaceState } from '../types/workspaceState';
import type { PlanNodeData, PlanEdgeData } from '../components/process/PlanGraphView';
import { adjacencyListToEdges } from './workspaceConversions';

export function derivePlanNodeStatus(node: DictNode): PlanNodeData['status'] {
  // Validation verdict takes precedence over raw delivery. A node can be
  // is_terminal (the workspace delivered via set_plan_node_output) yet have a
  // FAILED or UNRESOLVED validation — reading a clean "Done" then is misleading
  // (a delivered NSFR Model Spec read "Done" with 10 flagged-unverified issues).
  // So a rejected validation → blocked, and review_needed (incl. the staleness
  // cascade that fires when an approved node's content changes) → needs review,
  // EVEN when the node is terminal. Only a delivered node with a clean/absent
  // verdict reads "Done".
  //
  // NOTE: this covers the case where the verdict lives on the plan node. The
  // per-workspace brief (WorkspaceBrief) layers on the report's grounding seal
  // too, because the plan node stays validation_status=approved after the report
  // is edited (no stale-propagation back to the plan node).
  const v = node.validation_status;
  const wv = node.workspace_validation_status;
  if (v === 'rejected' || wv === 'rejected') return 'blocked';
  if (v === 'review_needed' || wv === 'review_needed') return 'needs_review';
  if (node.is_terminal === true) return 'done';
  if (v === 'approved' || wv === 'validated') return 'done';
  // The child workspace has produced its first deliverable but is not validated
  // yet → it is being worked = "In progress", not "Planned".
  if (node.workspace_started === true) return 'active';
  return 'planned';
}

// Backend stores text content HTML-escaped (e.g. "Retention &amp; STS spec");
// the plan graph renders labels as plain text (JSX / SVG <text>), so the entity
// shows through literally. Decode the common entities at this view boundary —
// the same convention already used in contentFormatter / SourcePanel /
// FindingsPanel. "&amp;" is decoded LAST so "&amp;lt;" can't collapse to "<".
export function decodeEntities(s: string): string {
  return s
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#0?39;/g, "'")
    .replace(/&#x27;/gi, "'")
    .replace(/&amp;/g, '&');
}

export function dictToPlanGraph(ws: DictWorkspaceState): { nodes: PlanNodeData[]; edges: PlanEdgeData[] } {
  const nodes: PlanNodeData[] = Object.values(ws.nodes || {}).map((n) => ({
    node_id: n.node_id,
    text: decodeEntities(n.label || (n as { name?: string }).name || ''),
    node_type: (n.node_type as PlanNodeData['node_type']) || 'task',
    status: derivePlanNodeStatus(n),
    workspace_id: n.workspace_id ?? null,
    workspace_type: n.workspace_type ?? null,
    cluster_id: n.cluster_id ?? null,
    validation_status: n.validation_status ?? null,
    workspace_validation_status: n.workspace_validation_status ?? null,
    workspace_started: n.workspace_started ?? false,
    completion_governance_status: n.completion_governance_status ?? null,
    completion_governance_reason: n.completion_governance_reason ?? null,
    completion_governance_scope: n.completion_governance_scope ?? null,
    // The node's deliverable — EvidenceBoard used this as the edge label.
    deliverable: n.deliverable ? decodeEntities(n.deliverable) : null,
    deliverable_role: n.deliverable_role ?? null,
    deliverable_shape_id: (n.metadata?.deliverable_shape_id as string | undefined) ?? null,
    deliverable_shape: (n.metadata?.deliverable_shape as string | undefined) ?? null,
    project: ws.project ?? null,
    validation_stats: (n.metadata?.validation_stats as Record<string, any> | undefined) ?? null,
    validator_stats: (n.metadata?.validator_stats as Record<string, any> | undefined) ?? null,
    validation_verdict: (n.metadata?.validation_verdict as string | undefined) ?? null,
    overall_verdict: (n.metadata?.overall_verdict as string | undefined) ?? null,
    deliverable_verdict: (n.metadata?.deliverable_verdict as string | undefined) ?? null,
    metadata: n.metadata ?? null,
    // add_plan_item stores the planner's full 2-6 sentence description in
    // LogicNode.content — the node detail panel renders it.
    description: n.content ? decodeEntities(n.content) : null,
    is_terminal: n.is_terminal ?? false,
  }));

  const edges: PlanEdgeData[] = adjacencyListToEdges(ws.edges || {}, ws.edge_metadata).map((e) => {
    const meta = ws.edge_metadata?.[e.source_node_id]?.[e.target_node_id];
    return {
      edge_id: `${e.source_node_id}->${e.target_node_id}`,
      from_node_id: e.source_node_id,
      to_node_id: e.target_node_id,
      edge_type: ((e as { edge_type?: string }).edge_type as PlanEdgeData['edge_type']) || 'feeds_into',
      label: (e as { label?: string }).label ? decodeEntities((e as { label?: string }).label as string) : null,
      // link_plan_nodes' per-edge handoff contract (1-3 sentences) — the
      // detail panel's Inputs/Output rows surface it.
      output_spec: meta?.output_spec ? decodeEntities(meta.output_spec) : undefined,
      has_output: Boolean(meta?.output_spec),
    };
  });

  return { nodes, edges };
}
